import logger from '../../lib/logger';
import React, { useState } from 'react';
import { UserPlus, UserMinus, Check, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const VOLUNTEER_AREAS = ["Event Coordination", "Youth Programs", "Madrassah Teaching", "Community Outreach", "Facilities Management", "Media & Communications", "Administrative Support", "Kitchen & Hospitality", "Security", "Other"];

const VolunteersTab = ({ volunteerDatabase, volunteerStats, loadingData, token, onRefresh, onStatsRefresh, onDashboardRefresh, apiBaseUrl }) => {
  const [showAddVolunteer, setShowAddVolunteer] = useState(false);
  const [newVolunteer, setNewVolunteer] = useState({ name: '', email: '', phone: '', area: '', notes: '' });

  const addVolunteer = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/admin/volunteer-database`, { 
        method: 'POST', 
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, 
        body: JSON.stringify(newVolunteer) 
      });
      if (response.ok) { 
        setNewVolunteer({ name: '', email: '', phone: '', area: '', notes: '' }); 
        setShowAddVolunteer(false); 
        onRefresh(); 
        onStatsRefresh(); 
        onDashboardRefresh(); 
      }
    } catch (err) { logger.error('Failed to add:', err); }
  };

  const toggleActive = async (id, currentStatus) => {
    try {
      await fetch(`${apiBaseUrl}/api/admin/volunteer-database/${id}`, { 
        method: 'PUT', 
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, 
        body: JSON.stringify({ is_active: !currentStatus }) 
      });
      onRefresh();
      onStatsRefresh();
    } catch (err) { logger.error('Failed to toggle:', err); }
  };

  const deleteVolunteer = async (id) => {
    if (!window.confirm('Are you sure you want to remove this record?')) return;
    try {
      await fetch(`${apiBaseUrl}/api/admin/volunteer-database/${id}`, { 
        method: 'DELETE', 
        headers: { 'Authorization': `Bearer ${token}` } 
      });
      onRefresh(); 
      onStatsRefresh(); 
      onDashboardRefresh();
    } catch (err) { logger.error('Failed to delete:', err); }
  };

  return (
    <div className="space-y-6">
      {volunteerStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 shadow border-l-4 border-green-500"><p className="text-sm text-[#5a6d62]">Total</p><p className="text-2xl font-bold text-[#1a2f23]">{volunteerStats.total}</p></div>
          <div className="bg-white p-4 shadow border-l-4 border-blue-500"><p className="text-sm text-[#5a6d62]">Active</p><p className="text-2xl font-bold text-green-600">{volunteerStats.active}</p></div>
          <div className="bg-white p-4 shadow border-l-4 border-gray-400"><p className="text-sm text-[#5a6d62]">Inactive</p><p className="text-2xl font-bold text-gray-500">{volunteerStats.inactive}</p></div>
          <div className="bg-white p-4 shadow border-l-4 border-[#c5a572]"><p className="text-sm text-[#5a6d62]">Areas</p><p className="text-2xl font-bold text-[#c5a572]">{volunteerStats.by_area?.length || 0}</p></div>
        </div>
      )}
      <div className="bg-white shadow-lg">
        <div className="p-6 border-b flex justify-between items-center">
          <div><h2 className="text-xl text-[#1a2f23]">Volunteer Database</h2></div>
          <Button onClick={() => setShowAddVolunteer(true)} className="btn-primary"><UserPlus className="w-4 h-4" /> Add Volunteer</Button>
        </div>
        {showAddVolunteer && (
          <div className="p-6 bg-[#f8f6f1] border-b">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><Label className="text-sm text-[#5a6d62]">Name *</Label><Input value={newVolunteer.name} onChange={(e) => setNewVolunteer({...newVolunteer, name: e.target.value})} className="mt-1" /></div>
              <div><Label className="text-sm text-[#5a6d62]">Email *</Label><Input type="email" value={newVolunteer.email} onChange={(e) => setNewVolunteer({...newVolunteer, email: e.target.value})} className="mt-1" /></div>
              <div><Label className="text-sm text-[#5a6d62]">Phone</Label><Input value={newVolunteer.phone} onChange={(e) => setNewVolunteer({...newVolunteer, phone: e.target.value})} className="mt-1" /></div>
              <div><Label className="text-sm text-[#5a6d62]">Area *</Label><Select value={newVolunteer.area} onValueChange={(v) => setNewVolunteer({...newVolunteer, area: v})}><SelectTrigger className="mt-1"><SelectValue placeholder="Select area" /></SelectTrigger><SelectContent>{VOLUNTEER_AREAS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent></Select></div>
              <div className="md:col-span-2"><Label className="text-sm text-[#5a6d62]">Notes</Label><Input value={newVolunteer.notes} onChange={(e) => setNewVolunteer({...newVolunteer, notes: e.target.value})} className="mt-1" /></div>
            </div>
            <div className="flex gap-2 mt-4"><Button onClick={addVolunteer} className="bg-green-600 hover:bg-green-700 text-white" disabled={!newVolunteer.name || !newVolunteer.email || !newVolunteer.area}><Check className="w-4 h-4" /> Save</Button><Button onClick={() => setShowAddVolunteer(false)} variant="outline">Cancel</Button></div>
          </div>
        )}
        {loadingData ? <div className="p-8 text-center"><Loader2 className="w-8 h-8 text-[#c5a572] animate-spin mx-auto" /></div> : volunteerDatabase.length === 0 ? <div className="p-8 text-center text-[#5a6d62]">No volunteers yet</div> : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#f8f6f1]"><tr><th className="px-6 py-3 text-left text-xs font-medium text-[#5a6d62] uppercase">Name</th><th className="px-6 py-3 text-left text-xs font-medium text-[#5a6d62] uppercase">Contact</th><th className="px-6 py-3 text-left text-xs font-medium text-[#5a6d62] uppercase">Area</th><th className="px-6 py-3 text-left text-xs font-medium text-[#5a6d62] uppercase">Status</th><th className="px-6 py-3 text-left text-xs font-medium text-[#5a6d62] uppercase">Actions</th></tr></thead>
              <tbody className="divide-y divide-gray-100">
                {volunteerDatabase.map(v => (
                  <tr key={v.id} className={!v.is_active ? 'bg-gray-50 opacity-60' : ''}>
                    <td className="px-6 py-4"><div className="text-sm font-medium text-[#1a2f23]">{v.name}</div></td>
                    <td className="px-6 py-4"><div className="text-sm text-[#5a6d62]">{v.email}</div><div className="text-xs text-[#5a6d62]">{v.phone || '-'}</div></td>
                    <td className="px-6 py-4"><span className="text-sm bg-[#c5a572]/20 text-[#8a7045] px-2 py-1 rounded">{v.area}</span></td>
                    <td className="px-6 py-4"><span className={`text-xs px-2 py-1 rounded ${v.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>{v.is_active ? 'Active' : 'Inactive'}</span></td>
                    <td className="px-6 py-4"><div className="flex gap-1"><button onClick={() => toggleActive(v.id, v.is_active)} className={`text-xs px-2 py-1 rounded ${v.is_active ? 'bg-gray-200 text-gray-700' : 'bg-green-100 text-green-700'}`}>{v.is_active ? 'Deactivate' : 'Activate'}</button><button onClick={() => deleteVolunteer(v.id)} className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded"><UserMinus className="w-3 h-3" /></button></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default VolunteersTab;
